import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { SlugField } from '@components/core-ui/form-elements/SlugField';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { useNameWithSlug } from '@helpers/useNameWithSlug';
import { useSlugAvailability } from '@helpers/useSlugAvailability';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { OrganizationsService } from 'app/API';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Content } from './styles/Content';

type TProps = {
  onCancel: () => void;
};

export const NewOrganizationModal = ({ onCancel }: TProps) => {
  const { call } = useAPI();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [queryError, setQueryError] = useState('');

  const { updateMe } = useAuthContext();
  const navigate = useNavigate();

  const { name, slug, setName, setSlug } = useNameWithSlug();

  const {
    status: slugStatus,
    isAvailable: isSlugAvailable,
    isUnverified: isSlugUnverified,
    retry: retrySlugCheck,
  } = useSlugAvailability({
    slug,
    checkAvailability: async (value) => {
      const available = await call(() => OrganizationsService.isSlugAvailable({ slug: value }));
      return !!available?.result;
    },
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const data = await call(() => OrganizationsService.create({ requestBody: { name, slug } }));
    if (!data.slug) {
      setIsSubmitting(false);
      setQueryError('Something went wrong');
      return;
    }
    setIsSubmitting(false);
    const me = await updateMe();
    if (!me) {
      onCancel();
      return;
    }
    const newOrg = me.organizations.find((org) => org.slug === data.slug);
    if (newOrg) {
      navigate(routes.dashboard.make(data.slug));
    }
    onCancel();
  };

  return (
    <ModalWrapper width="400px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Create New Organization</ModalTitle>
      <Content>
        <div className="workspaceSettings-inputWrapper">
          <div className="inputContainer">
            <label htmlFor="title">Organization Name</label>
            <Input
              label="Name"
              name="title"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </div>

          <div className="inputContainer">
            <label htmlFor="slug">Organization ID</label>
            <SlugField
              label="Organization ID"
              value={slug}
              status={slugStatus}
              onChange={setSlug}
              onRetry={retrySlugCheck}
            />
          </div>

          {queryError && <div className="workspaceSettings-errorMessage largeText">{queryError}</div>}
        </div>
        <ModalFooter>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button
            disabled={isSubmitting || isSlugUnverified || !isSlugAvailable || !(slug && name)}
            onClick={() => handleSubmit()}
          >
            Create
          </Button>
        </ModalFooter>
      </Content>
    </ModalWrapper>
  );
};
